/**
 * Caregiver ClientsPage (CONTRACTS §6, spec §9.2):
 * - invite flow: inviteClient by the client's registered email;
 * - link list with pending / active / revoked states;
 * - revoke with an explicit destructive confirmation;
 * - the client-side view of links ('Mijn koppelingen als cliënt') with
 *   accept / revoke per product policy.
 *
 * All relationship changes go through repositories.caregiver — RLS is the
 * enforcement layer and every change is audit-logged server-side.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  ConfirmDestructiveAction,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusChip,
  Tabs,
  TextField,
  useToast,
} from '@/components/ui'
import { UserIcon, UsersIcon } from '@/components/icons'
import { repositories } from '@/services/repositories'
import { PATHS } from '@/app/router/paths'
import type { CaregiverClientLink, LinkStatus } from '@/types'
import { errorMessage, formatDutchDateTime, isNotConfigured, type LoadStatus } from '@/features/medication'
import { useLinkedClients } from './useLinkedClients'
import './caregiver.css'

const LINK_STATUS_META: Record<LinkStatus, { label: string; chip: 'success' | 'warning' | 'neutral' }> = {
  pending: { label: 'Uitnodiging verstuurd', chip: 'warning' },
  active: { label: 'Actief gekoppeld', chip: 'success' },
  revoked: { label: 'Koppeling beëindigd', chip: 'neutral' },
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function linkStatusLabel(status: LinkStatus, link: CaregiverClientLink): string {
  if (status === 'pending') return `Uitgenodigd op ${formatDutchDateTime(link.createdAt)}`
  if (status === 'active' && link.acceptedAt) return `Actief sinds ${formatDutchDateTime(link.acceptedAt)}`
  if (status === 'revoked' && link.revokedAt) return `Beëindigd op ${formatDutchDateTime(link.revokedAt)}`
  return ''
}

export default function ClientsPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [tab, setTab] = useState('caregiver')

  return (
    <div className="caregiver-page">
      <header>
        <h1 className="caregiver-section-title">Cliënten en koppelingen</h1>
        <p className="caregiver-muted">
          Beheer welke cliënten met jou zijn gekoppeld en welke koppelingen aan jou zijn
          aangeboden.
        </p>
      </header>
      <Tabs
        value={tab}
        onChange={setTab}
        ariaLabel="Koppelingen"
        tabs={[
          { value: 'caregiver', label: 'Mijn cliënten' },
          { value: 'client', label: 'Mijn begeleiders' },
        ]}
      />
      {tab === 'caregiver' ? (
        <CaregiverLinks onOpenClient={(id) => navigate(PATHS.caregiver.clientDetail(id))} showToast={showToast} />
      ) : (
        <ClientLinks showToast={showToast} />
      )}
    </div>
  )
}

type ToastFn = (toast: { message: string; tone?: 'success' | 'error' | 'info' }) => void

function useMyLinks(asRole: 'caregiver' | 'client') {
  const [state, setState] = useState<{ status: LoadStatus; links: CaregiverClientLink[]; notConfigured: boolean; error: string | null }>({
    status: 'loading',
    links: [],
    notConfigured: false,
    error: null,
  })

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading', error: null }))
    try {
      const links = await repositories.caregiver.listMyLinks(asRole)
      setState({ status: 'ready', links, notConfigured: false, error: null })
    } catch (error) {
      if (isNotConfigured(error)) {
        setState({ status: 'unavailable', links: [], notConfigured: true, error: null })
      } else {
        setState({ status: 'error', links: [], notConfigured: false, error: errorMessage(error) })
      }
    }
  }, [asRole])

  useEffect(() => {
    void load()
  }, [load])

  return useMemo(() => ({ ...state, refresh: load }), [state, load])
}

function CaregiverLinks({ onOpenClient, showToast }: { onOpenClient: (clientId: string) => void; showToast: ToastFn }) {
  const { status, links, refresh } = useMyLinks('caregiver')
  const clients = useLinkedClients()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<CaregiverClientLink | null>(null)
  const [busy, setBusy] = useState(false)

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const client of clients.clients) {
      map.set(client.id, client.displayName?.trim() || 'Cliënt')
    }
    return map
  }, [clients.clients])

  const sortedLinks = useMemo(() => {
    const order: Record<LinkStatus, number> = { pending: 0, active: 1, revoked: 2 }
    return [...links].sort(
      (a, b) => order[a.status] - order[b.status] || b.createdAt.localeCompare(a.createdAt),
    )
  }, [links])

  const invite = async () => {
    if (!isValidEmail(email)) {
      setEmailError('Vul het geldige e-mailadres in waarmee de cliënt is geregistreerd.')
      return
    }
    setEmailError(null)
    setInviting(true)
    try {
      await repositories.caregiver.inviteClient(email.trim())
      setEmail('')
      showToast({ message: 'Uitnodiging verstuurd. De cliënt moet de koppeling accepteren.', tone: 'success' })
      await refresh()
    } catch (err) {
      setEmailError(errorMessage(err))
    } finally {
      setInviting(false)
    }
  }

  const revoke = async () => {
    if (!revokeTarget || busy) return
    setBusy(true)
    try {
      await repositories.caregiver.revokeLink(revokeTarget.id)
      showToast({ message: 'Koppeling beëindigd. De toegang is direct ingetrokken.', tone: 'info' })
      setRevokeTarget(null)
      await refresh()
    } catch (err) {
      showToast({ message: errorMessage(err), tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* Invite flow */}
      <Card padding="md">
        <div className="caregiver-form">
          <h2 className="caregiver-section-title">Cliënt uitnodigen</h2>
          <p className="caregiver-muted">
            De cliënt krijgt een melding en moet de koppeling zelf accepteren voordat je gegevens
            kunt zien.
          </p>
          <TextField
            label="E-mailadres van de cliënt"
            type="email"
            value={email}
            onChange={setEmail}
            error={emailError ?? undefined}
            autoComplete="off"
            placeholder="naam@voorbeeld.nl"
            required
          />
          <Button variant="primary" loading={inviting} onClick={() => void invite()}>
            Uitnodiging versturen
          </Button>
        </div>
      </Card>

      {/* Link states */}
      {status === 'loading' ? <Skeleton lines={3} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">Cloud niet ingesteld. Koppelingen kunnen nu niet worden opgehaald.</p>
        </Card>
      ) : null}
      {status === 'error' ? (
        <ErrorState title="Koppelingen laden mislukt" onRetry={() => void refresh()} />
      ) : null}
      {status === 'ready' ? (
        sortedLinks.length === 0 ? (
          <EmptyState
            icon={<UsersIcon size={26} />}
            title="Nog geen koppelingen"
            body="Nodig hierboven een cliënt uit om te beginnen."
          />
        ) : (
          <Card padding="sm">
            <ul className="caregiver-list" aria-label="Koppelingen">
              {sortedLinks.map((link) => {
                const meta = LINK_STATUS_META[link.status]
                const name = clientNameById.get(link.clientId)
                return (
                  <li key={link.id} className="caregiver-link-item">
                    <div className="caregiver-link-header">
                      <span className="caregiver-client-avatar" aria-hidden="true">
                        <UserIcon size={20} />
                      </span>
                      <span className="caregiver-link-email">
                        {name ?? `Koppeling van ${formatDutchDateTime(link.createdAt)}`}
                      </span>
                      <StatusChip status={meta.chip}>{meta.label}</StatusChip>
                    </div>
                    <p className="caregiver-muted">{linkStatusLabel(link.status, link)}</p>
                    <div className="caregiver-actions">
                      {link.status === 'active' ? (
                        <Button variant="secondary" size="sm" onClick={() => onOpenClient(link.clientId)}>
                          Open cliënt
                        </Button>
                      ) : null}
                      {link.status !== 'revoked' ? (
                        <Button variant="danger" size="sm" onClick={() => setRevokeTarget(link)}>
                          Koppeling beëindigen
                        </Button>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          </Card>
        )
      ) : null}

      <ConfirmDestructiveAction
        open={revokeTarget !== null}
        title="Koppeling beëindigen"
        body="De toegang tot deze cliënt wordt direct ingetrokken en vastgelegd in het auditlog. De cliënt kan daarna opnieuw worden uitgenodigd."
        confirmLabel="Beëindigen"
        onConfirm={() => void revoke()}
        onCancel={() => setRevokeTarget(null)}
      />
    </>
  )
}

function ClientLinks({ showToast }: { showToast: ToastFn }) {
  const { status, links, refresh } = useMyLinks('client')
  const [busyLinkId, setBusyLinkId] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<CaregiverClientLink | null>(null)

  const sortedLinks = useMemo(
    () => [...links].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [links],
  )

  const accept = async (link: CaregiverClientLink) => {
    setBusyLinkId(link.id)
    try {
      await repositories.caregiver.acceptLink(link.id)
      showToast({ message: 'Koppeling geaccepteerd. Je begeleider kan nu je medicatiegegevens zien.', tone: 'success' })
      await refresh()
    } catch (err) {
      showToast({ message: errorMessage(err), tone: 'error' })
    } finally {
      setBusyLinkId(null)
    }
  }

  const revoke = async () => {
    if (!revokeTarget) return
    setBusyLinkId(revokeTarget.id)
    try {
      await repositories.caregiver.revokeLink(revokeTarget.id)
      showToast({ message: 'Koppeling beëindigd. De begeleider ziet je gegevens niet meer.', tone: 'info' })
      setRevokeTarget(null)
      await refresh()
    } catch (err) {
      showToast({ message: errorMessage(err), tone: 'error' })
    } finally {
      setBusyLinkId(null)
    }
  }

  if (status === 'loading') return <Skeleton lines={3} />
  if (status === 'unavailable') {
    return (
      <Card padding="md">
        <p className="caregiver-muted">Cloud niet ingesteld. Koppelingen kunnen nu niet worden opgehaald.</p>
      </Card>
    )
  }
  if (status === 'error') {
    return <ErrorState title="Koppelingen laden mislukt" onRetry={() => void refresh()} />
  }
  if (sortedLinks.length === 0) {
    return (
      <EmptyState
        icon={<UserIcon size={26} />}
        title="Geen begeleiders gekoppeld"
        body="Wanneer een begeleider jou uitnodigt met jouw e-mailadres, verschijnt die uitnodiging hier."
      />
    )
  }

  return (
    <>
      <Card padding="sm">
        <ul className="caregiver-list" aria-label="Mijn begeleiders">
          {sortedLinks.map((link) => {
            const meta = LINK_STATUS_META[link.status]
            return (
              <li key={link.id} className="caregiver-link-item">
                <div className="caregiver-link-header">
                  <span className="caregiver-client-avatar" aria-hidden="true">
                    <UserIcon size={20} />
                  </span>
                  <span className="caregiver-link-email">
                    Koppeling van {formatDutchDateTime(link.createdAt)}
                  </span>
                  <StatusChip status={meta.chip}>{meta.label}</StatusChip>
                </div>
                {link.status === 'pending' ? (
                  <p className="caregiver-muted">
                    Een begeleider wil met jou koppelen. Alleen als je accepteert kan deze jouw
                    medicatiegegevens zien. Dit wordt vastgelegd in het auditlog.
                  </p>
                ) : (
                  <p className="caregiver-muted">{linkStatusLabel(link.status, link)}</p>
                )}
                <div className="caregiver-actions">
                  {link.status === 'pending' ? (
                    <Button
                      variant="primary"
                      size="sm"
                      loading={busyLinkId === link.id}
                      onClick={() => void accept(link)}
                    >
                      Accepteren
                    </Button>
                  ) : null}
                  {link.status !== 'revoked' ? (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busyLinkId === link.id}
                      onClick={() => setRevokeTarget(link)}
                    >
                      {link.status === 'pending' ? 'Afwijzen' : 'Koppeling beëindigen'}
                    </Button>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      </Card>

      <ConfirmDestructiveAction
        open={revokeTarget !== null}
        title={revokeTarget?.status === 'pending' ? 'Uitnodiging afwijzen' : 'Koppeling beëindigen'}
        body="De begeleider krijgt geen toegang tot jouw gegevens, of verliest de bestaande toegang direct. Dit wordt vastgelegd in het auditlog."
        confirmLabel={revokeTarget?.status === 'pending' ? 'Afwijzen' : 'Beëindigen'}
        onConfirm={() => void revoke()}
        onCancel={() => setRevokeTarget(null)}
      />
    </>
  )
}
